import { useEffect, useState } from 'react';
import { LogOut } from 'lucide-react';
import App from './App';
import { Mark } from './components/ui';
import { useTheme } from './services/theme';
import { setApiOrigin, setHosted } from './services/apiOrigin';

const TOKEN_KEY = 'platform_auth_token';
const PROJECT_KEY = 'compass.project';
const PLATFORM = 'solido';
const CENTER = ((import.meta as any).env?.VITE_CENTER_URL as string | undefined) || 'https://api.solido.dev';

interface Claims { sub?: string; email?: string; directory?: string; exp?: number }

function claimsOf(token: string | null): Claims | null {
 if (!token) return null;
 try {
  const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(part.padEnd(part.length + ((4 - (part.length % 4)) % 4), '=')));
 } catch {
  return null;
 }
}

function stored(key: string): string | null {
 try {
  return localStorage.getItem(key);
 } catch {
  return null;
 }
}

function keep(key: string, value: string | null) {
 try {
  if (value === null) localStorage.removeItem(key);
  else localStorage.setItem(key, value);
 } catch {
  return;
 }
}

function live(token: string | null): Claims | null {
 const c = claimsOf(token);
 if (!c || !c.directory) return null;
 if (c.exp && c.exp * 1000 < Date.now()) return null;
 return c;
}

async function center(path: string, body: unknown): Promise<any> {
 let r: Response;
 try {
  r = await fetch(`${CENTER}/api/auth${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
 } catch (e: any) {
  throw new Error(`the center at ${CENTER} did not answer — ${e?.message ?? e}`);
 }
 const j: any = await r.json().catch(() => ({}));
 if (!r.ok || j?.success === false) throw new Error(j?.error || j?.message || `the center answered HTTP ${r.status}`);
 return j;
}

const tokenOf = (j: any): string | null => j?.accessToken ?? j?.data?.tokens?.accessToken ?? null;

function SignIn({ onToken }: { onToken: (t: string) => void }) {
 const [email, setEmail] = useState('');
 const [password, setPassword] = useState('');
 const [code, setCode] = useState('');
 const [sent, setSent] = useState(false);
 const [withPassword, setWithPassword] = useState(false);
 const [busy, setBusy] = useState(false);
 const [said, setSaid] = useState<string | null>(null);

 async function run(work: () => Promise<void>) {
  setBusy(true);
  setSaid(null);
  try {
   await work();
  } catch (e: any) {
   setSaid(e?.message ?? String(e));
  } finally {
   setBusy(false);
  }
 }

 const signIn = () => run(async () => {
  const t = tokenOf(await center('/login', { email: email.trim(), password }));
  if (!t) throw new Error('the center signed you in but gave no token');
  onToken(t);
 });
 const sendCode = () => run(async () => {
  await center('/magic/request', { email: email.trim() });
  setSent(true);
  setSaid(null);
 });
 const useCode = () => run(async () => {
  const t = tokenOf(await center('/magic/verify', { email: email.trim(), token: code.trim() }));
  if (!t) throw new Error('the center took the code but gave no token');
  onToken(t);
 });

 return (
  <div className="min-h-screen flex items-center justify-center px-4">
   <form
    onSubmit={(e) => {
     e.preventDefault();
     if (withPassword) { if (password) signIn(); }
     else if (sent) { if (code.trim()) useCode(); }
     else if (email.trim()) sendCode();
    }}
    className="w-full max-w-sm bg-surface border border-line rounded-card shadow-panel p-7 flex flex-col gap-3"
   >
    <div className="flex items-center gap-2">
     <Mark />
     <h1 className="text-xl font-semibold tracking-tight">Compass</h1>
    </div>
    <p className="m-0 text-sm text-mute">
     {sent && !withPassword ? `Enter the code we sent to ${email.trim()}.` : 'Sign in to your project. We email you a code — no password needed.'}
    </p>
    <label className="flex flex-col gap-1 text-sm">
     Work email
     <input type="email" autoComplete="email" required value={email} disabled={sent && !withPassword} onChange={(e) => setEmail(e.target.value)} className="px-3 py-2.5 bg-surface text-ink border border-line2 rounded-lg disabled:bg-inset" />
    </label>
    {withPassword ? (
     <>
      <label className="flex flex-col gap-1 text-sm">
       Password
       <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="px-3 py-2.5 bg-surface text-ink border border-line2 rounded-lg" />
      </label>
      <button type="submit" disabled={busy || !email.trim() || !password} className="min-h-11 px-4 text-sm font-semibold bg-teal text-teal-on rounded-full disabled:opacity-50">{busy ? 'Signing in…' : 'Sign in'}</button>
      <button type="button" onClick={() => { setWithPassword(false); setSaid(null); }} className="text-sm text-teal underline">Email me a code instead</button>
     </>
    ) : !sent ? (
     <button type="submit" disabled={busy || !email.trim()} className="min-h-11 px-4 text-sm font-semibold bg-teal text-teal-on rounded-full disabled:opacity-50">{busy ? 'Sending…' : 'Email me a code'}</button>
    ) : (
     <>
      <label className="flex flex-col gap-1 text-sm">
       Code
       <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} className="px-3 py-2.5 bg-surface text-ink border border-line2 rounded-lg" />
      </label>
      <button type="submit" disabled={busy || !code.trim()} className="min-h-11 px-4 text-sm font-semibold bg-teal text-teal-on rounded-full disabled:opacity-50">{busy ? 'Checking…' : 'Sign in'}</button>
      <button type="button" onClick={() => { setSent(false); setCode(''); setSaid(null); }} className="text-sm text-teal underline">Use another email</button>
     </>
    )}
    {said && <span role="status" className="text-sm text-signal">{said}</span>}
    <div className="flex items-center justify-between pt-2 border-t border-line text-sm">
     <a href="https://www.solido.dev/#start" className="text-teal underline">New here? Start a project</a>
     {!withPassword && <button type="button" onClick={() => { setWithPassword(true); setSent(false); setSaid(null); }} className="text-mute2 underline">Use a password</button>}
    </div>
   </form>
  </div>
 );
}

function PickProject({ who, onPick, onSignOut }: { who: string; onPick: (p: string) => void; onSignOut: () => void }) {
 const [value, setValue] = useState(stored(PROJECT_KEY) ?? '');
 const ok = /^[a-z0-9][a-z0-9-]{0,62}$/.test(value.trim());
 return (
  <div className="min-h-screen flex items-center justify-center px-4">
   <form onSubmit={(e) => { e.preventDefault(); if (ok) onPick(value.trim()); }} className="w-full max-w-sm bg-surface border border-line rounded-card shadow-panel p-7 flex flex-col gap-3">
    <h1 className="m-0 text-xl font-semibold tracking-tight">Which project?</h1>
    <p className="m-0 text-sm text-mute">{who} signs in for solido, so Compass asks which project to open.</p>
    <label className="flex flex-col gap-1 text-sm">
     Project
     <input value={value} onChange={(e) => setValue(e.target.value.toLowerCase())} placeholder="oja" className="px-3 py-2.5 bg-surface text-ink border border-line2 rounded-lg" />
    </label>
    <button type="submit" disabled={!ok} className="min-h-11 px-4 text-sm font-semibold bg-teal text-teal-on rounded-full disabled:opacity-50">Open</button>
    <button type="button" onClick={onSignOut} className="text-sm text-mute underline">Sign out</button>
   </form>
  </div>
 );
}

export default function Standalone() {
 const [token, setToken] = useState<string | null>(() => (live(stored(TOKEN_KEY)) ? stored(TOKEN_KEY) : null));
 const [picked, setPicked] = useState<string | null>(null);
 const claims = live(token);
 const th = useTheme();
 setHosted(false);
 setApiOrigin('/compass-api');

 useEffect(() => {
  if (!claims?.exp) return undefined;
  const t = setTimeout(() => setToken(null), Math.max(0, claims.exp * 1000 - Date.now()));
  return () => clearTimeout(t);
 }, [claims?.exp]);

 function signedIn(t: string) {
  keep(TOKEN_KEY, t);
  setToken(t);
 }

 function signOut() {
  keep(TOKEN_KEY, null);
  setToken(null);
  setPicked(null);
 }

 const shell = (inner: JSX.Element) => <div className="compass-root min-h-screen flex flex-col" data-theme={th.chosen ?? undefined}>{inner}</div>;
 if (!claims) return shell(<SignIn onToken={signedIn} />);
 const who = claims.email ?? claims.sub ?? 'this account';
 const project = claims.directory === PLATFORM ? picked : claims.directory!;
 if (!project) {
  return shell(<PickProject who={who} onSignOut={signOut} onPick={(p) => { keep(PROJECT_KEY, p); setPicked(p); }} />);
 }

 return (
  <div className="compass-root min-h-screen flex flex-col" data-theme={th.chosen ?? undefined}>
   <div className="flex items-center gap-3 px-8 py-1.5 bg-surface border-b border-line text-mute2 text-xs">
    <span>{who}</span>
    {claims.directory === PLATFORM && <button type="button" onClick={() => setPicked(null)} className="underline">another project</button>}
    <div className="flex-1" />
    <button type="button" onClick={signOut} className="flex items-center gap-1 underline"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
   </div>
   <App key={project} projectId={project} seat={claims.directory === PLATFORM ? 'specdev' : 'customer'} themeState={th} />
  </div>
 );
}
